import React, { useState, useRef, useEffect } from "react";
import CustomAlert from "../../../components/Common/CustomAlert";
import CheckRender from "../../../components/Common/CheckRender";
import useEmpresa from "../../../hooks/useEmpresa";
import Text from "../../../components/Common/Text";
import {
  FileUploadContainer,
  DropZone,
  FileList,
  FileItem,
  FileInfo,
  RemoveButton,
  FileIcon,
} from "../styles/FileUploadStyles";

const FileUploadComponent = ({
  onFilesChange,
  maxFiles = 10,
  maxSizeMB = 7.78,
  initialFiles = [],
  archivosEnviados = new Set(),
}) => {
  const empresa = useEmpresa();
  const [files, setFiles] = useState(initialFiles);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState(null);
  const fileInputRef = useRef(null);

  const allowedExtensions = [".png", ".jpg", ".jpeg", ".pdf"];

  useEffect(() => {
    setFiles(initialFiles);
  }, [initialFiles]);

  const validateFile = (file) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    if (!allowedExtensions.includes(fileExtension)) {
      return {
        valid: false,
        error: "Extensión de archivo no permitida",
      };
    }

    if (file.size > maxSizeBytes) {
      return {
        valid: false,
        error:
          "El archivo que intentas adjuntar supera el peso máximo permitido (7.78 MB). Por favor, selecciona un archivo más liviano o comprímelo antes de cargarlo",
      };
    }

    if (files.some((f) => f.name === file.name)) {
      return {
        valid: false,
        error: "Archivo duplicado",
      };
    }

    return { valid: true };
  };

  const showErrorAlert = (message) => {
    setAlertConfig({
      type: "error",
      title: "Error",
      message: message,
      onConfirm: () => {
        setShowAlert(false);
      },
    });
    setShowAlert(true);
  };

  const handleFiles = (newFiles) => {
    const fileArray = Array.from(newFiles);

    if (files.length + fileArray.length > maxFiles) {
      showErrorAlert(
        `Recuerda que puedes anexar un máximo de 10 soportes por solicitud. Si necesitas agregar más, comunícate con nuestros oficiales al 809-476-3535 para más información.`
      );
      return;
    }

    const validFiles = [];
    const invalidFiles = [];

    fileArray.forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file);
        showErrorAlert(`${validation.error}. Archivo: ${file.name}`);
      }
    });

    if (validFiles.length > 0) {
      const updatedFiles = [...files, ...validFiles];
      setFiles(updatedFiles);
      onFilesChange(updatedFiles);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = e.dataTransfer.files;
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = e.target.files;
    handleFiles(selectedFiles);
    e.target.value = "";
  };

  const removeFile = (index) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    onFilesChange(updatedFiles);
  };

  const getFileIcon = (file) => {
    const extension = "." + file.name.split(".").pop().toLowerCase();
    if ([".png", ".jpg", ".jpeg"].includes(extension)) {
      return "🖼️";
    } else if (extension === ".pdf") {
      return "📄";
    }
    return "📎";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <FileUploadContainer>
      <DropZone
        isDragOver={isDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <div style={{ textAlign: "center" }}>
          <Text
            size="16px"
            color={isDragOver ? empresa?.COLOR_PRINCIPAL : "#666"}
          >
            {isDragOver
              ? "Suelta los archivos aquí"
              : "Arrastra archivos aquí o haz clic para seleccionar"}
          </Text>
          <Text size="14px" color="#999" margin="5px 0 0 0">
            Tipos permitidos: PNG, JPG, JPEG, PDF (máx. {maxSizeMB} MB)
          </Text>
        </div>
      </DropZone>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".png,.jpg,.jpeg,.pdf"
        onChange={handleFileInput}
        style={{ display: "none" }}
      />

      {files.length > 0 && (
        <FileList>
          {files.map((file, index) => {
            const fileId = file.id;
            const fileName = file.name;
            const hasFileId = fileId && archivosEnviados.has(fileId);
            const hasFileName = fileName && archivosEnviados.has(fileName);
            const isFileSent = hasFileId || hasFileName;
            
            return (
              <FileItem key={index}>
                <FileIcon>{getFileIcon(file)}</FileIcon>
                <FileInfo>
                  <Text size="14px" weight="500">
                    {file.name}
                  </Text>
                  <Text size="12px" color="#666">
                    {formatFileSize(file.size)}
                  </Text>
                </FileInfo>
                <CheckRender allowed={!isFileSent}>
                  <RemoveButton type="button" onClick={() => removeFile(index)}>✕</RemoveButton>
                </CheckRender>
              </FileItem>
            );
          })}
        </FileList>
      )}

      <CheckRender allowed={showAlert}>
        <CustomAlert {...alertConfig} maxWidth="500px" />
      </CheckRender>
    </FileUploadContainer>
  );
};

export default FileUploadComponent;
